import React, { useState, useEffect } from 'react';
import { useToast } from '../App';
import api from '../utils/api';
import LoadingState from '../components/LoadingState';

const Brands = () => {
    const { showSuccess, showError } = useToast();
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingBrand, setEditingBrand] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        description: '',
        thumbnail_id: null,
    });

    useEffect(() => {
        loadBrands();
    }, []);

    const loadBrands = async () => {
        try {
            setLoading(true);
            const response = await api.getBrands();
            if (response.success) {
                setBrands(response.brands || []);
            }
        } catch (error) {
            showError('Failed to load brands');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingBrand) {
                const response = await api.updateBrand(editingBrand.id, formData);
                if (response.success) {
                    showSuccess('Brand updated successfully');
                    loadBrands();
                    resetForm();
                }
            } else {
                const response = await api.createBrand(formData);
                if (response.success) {
                    showSuccess('Brand created successfully');
                    loadBrands();
                    resetForm();
                }
            }
        } catch (error) {
            showError(error.message || 'Failed to save brand');
        }
    };

    const handleEdit = (brand) => {
        setEditingBrand(brand);
        setFormData({
            name: brand.name,
            slug: brand.slug,
            description: brand.description || '',
            thumbnail_id: brand.thumbnail_id || null,
        });
        setThumbnailPreview(brand.thumbnail_url || null);
        setShowForm(true);
    };

    const handleDelete = async (brandId) => {
        if (!confirm('Are you sure you want to delete this brand?')) return;

        try {
            const response = await api.deleteBrand(brandId);
            if (response.success) {
                showSuccess('Brand deleted successfully');
                loadBrands();
            }
        } catch (error) {
            showError('Failed to delete brand');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            slug: '',
            description: '',
            thumbnail_id: null,
        });
        setEditingBrand(null);
        setShowForm(false);
        setThumbnailPreview(null);
    };

    const openMediaLibrary = () => {
        if (window.wp && window.wp.media) {
            const frame = window.wp.media({
                title: 'Select Brand Logo',
                button: { text: 'Use this image' },
                multiple: false
            });

            frame.on('select', function() {
                const attachment = frame.state().get('selection').first().toJSON();
                setFormData(prev => ({
                    ...prev,
                    thumbnail_id: attachment.id
                }));
                setThumbnailPreview(attachment.url);
            });

            frame.open();
        }
    };

    const removeThumbnail = () => {
        setFormData(prev => ({
            ...prev,
            thumbnail_id: null
        }));
        setThumbnailPreview(null);
    };

    return (
        <div className="page brands-page">
            <div className="page-container">
                <div className="page-header">
                    <div className="page-title">
                        <h1>Brands</h1>
                        <p>Manage product brands</p>
                    </div>
                    <div className="page-actions">
                        <button 
                            className="btn btn-primary"
                            onClick={() => setShowForm(!showForm)}
                        >
                            {showForm ? 'Cancel' : '+ Add Brand'}
                        </button>
                    </div>
                </div>

                {showForm && (
                    <div className="taxonomy-form-section">
                        <h3>{editingBrand ? 'Edit Brand' : 'Add New Brand'}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Name *</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        required
                                        placeholder="Enter brand name"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Slug</label>
                                    <input
                                        type="text"
                                        value={formData.slug}
                                        onChange={(e) => setFormData({...formData, slug: e.target.value})}
                                        placeholder="Auto-generated from name"
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    rows="3"
                                    placeholder="Optional brand description"
                                />
                            </div>
                            <div className="form-group">
                                <label>Brand Logo</label>
                                {thumbnailPreview ? (
                                    <div className="thumbnail-preview">
                                        <img src={thumbnailPreview} alt="Brand logo" />
                                        <div className="thumbnail-actions">
                                            <button type="button" className="btn btn-secondary btn-sm" onClick={openMediaLibrary}>
                                                Change Logo
                                            </button>
                                            <button type="button" className="btn btn-danger btn-sm" onClick={removeThumbnail}>
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button type="button" className="btn btn-secondary" onClick={openMediaLibrary}>
                                        Choose Logo
                                    </button>
                                )}
                            </div>
                            <div className="form-actions">
                                <button type="submit" className="btn btn-primary">
                                    {editingBrand ? 'Update' : 'Create'} Brand
                                </button>
                                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div className="taxonomy-list">
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <LoadingState message="Loading brands..." />
                        </div>
                    ) : brands.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">🎨</div>
                            <h3>No brands yet</h3>
                            <p>Create your first brand to get started</p>
                        </div>
                    ) : (
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Slug</th>
                                    <th>Description</th>
                                    <th>Count</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {brands.map(brand => (
                                    <tr key={brand.id}>
                                        <td>
                                            {brand.thumbnail_url && (
                                                <img src={brand.thumbnail_url} alt="" style={{width: '30px', height: '30px', marginRight: '10px', objectFit: 'cover', borderRadius: '4px'}} />
                                            )}
                                            <strong>{brand.name}</strong>
                                        </td>
                                        <td>{brand.slug}</td>
                                        <td>{brand.description || '-'}</td>
                                        <td>{brand.count}</td>
                                        <td>
                                            <button 
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => handleEdit(brand)}
                                            >
                                                Edit
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-danger"
                                                onClick={() => handleDelete(brand.id)}
                                                style={{marginLeft: '8px'}}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Brands;
